import { humanizeError } from '@/lib/errors';
import type { StudentImport } from '@/api/users';

/**
 * Логика экрана «Импорт учеников» файлом (договор students-import.md) —
 * отдельно от разметки, чтобы проверяться без браузера.
 */

/**
 * Текст отказа для человека.
 *
 * ⚠️ СВОЙ РАЗБОР, А НЕ humanizeError. Сервер импорта отдаёт отказ как
 * `{status, code, message}`, и `message` уже человеческими словами (§0, §7).
 * Общий humanizeError любой 409 превращает в «такая запись уже есть» — и
 * «на этот марафон уже идёт загрузка — дождитесь конца» пропал бы, а человек
 * решил бы, что ученики уже заведены. Поэтому: есть `code` и `message` —
 * показываем `message` дословно; нет (403 из middleware, обрыв сети) — общий
 * разбор.
 */
export function importErrorText(err: unknown): string {
  const raw = (err as { raw?: unknown } | null)?.raw;
  if (raw && typeof raw === 'object') {
    const r = raw as { code?: unknown; message?: unknown };
    if (typeof r.code === 'string' && r.code && typeof r.message === 'string' && r.message.trim()) {
      return r.message.trim();
    }
  }
  return humanizeError(err);
}

/** Набор, на котором прошёл пробный прогон. */
export type ImportInputs = { file: File | null; marathonId: string; accessUntil: string };

/**
 * Тот же ли набор, что проверяли.
 *
 * ⚠️ ФАЙЛ СРАВНИВАЕТСЯ ССЫЛКОЙ, А НЕ ИМЕНЕМ. Человек поправил список в Excel и
 * выбрал «spisok.csv» снова — имя то же, строки другие. Запись без нового
 * пробного прогона увезла бы то, чего он не видел.
 */
export function sameInputs(a: ImportInputs | null, b: ImportInputs): boolean {
  return !!a && !!a.file && a.file === b.file && a.marathonId === b.marathonId && a.accessUntil === b.accessUntil;
}

/**
 * Марафон из адреса `/admin/imports/new?marathon_id=…` — только если он есть в
 * выборе на этом экране.
 *
 * ⚠️ ЧУЖОЙ ID МОЛЧА НЕ ПОДСТАВЛЯЕМ. В выборе только рабочие курсы: без архивных
 * и без шаблонов. `<select>` со значением, которого нет среди `<option>`,
 * рисуется ПУСТЫМ, а значение при этом живёт в состоянии — и «Проверить»
 * открывается. Файл уехал бы на марафон, которого человек на экране не видел.
 * Не нашли — пусто, марафон выбирается руками.
 */
export function marathonFromQuery(wanted: string | null | undefined, options: { id: string }[]): string {
  const id = (wanted ?? '').trim();
  if (!id) return '';
  return options.some((m) => m.id === id) ? id : '';
}

export function isImportFinished(status: StudentImport['status'] | undefined): boolean {
  return status === 'done' || status === 'failed';
}

/** Завтрашняя дата `ГГГГ-ММ-ДД` по местному календарю — нижняя граница поля «доступ до». */
export function tomorrowISO(now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
